import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ChessBoard from '@/components/chess/ChessBoard';
import MoveHistory from '@/components/chess/MoveHistory';
import EvalBar from '@/components/chess/EvalBar';
import CoachFeedback from '@/components/chess/CoachFeedback';
import ControlBar from '@/components/chess/ControlBar';
import NewGameDialog from '@/components/chess/NewGameDialog';
import SaveLoadDialog from '@/components/chess/SaveLoadDialog';
import GameOverDialog from '@/components/chess/GameOverDialog';
import MoveQualityCard from '@/components/chess/MoveQualityCard';
import GameReview from '@/components/chess/GameReview';
import StatsDialog from '@/components/chess/StatsDialog';
import LeaderboardDialog from '@/components/chess/LeaderboardDialog';
import MultiplayerDialog from '@/components/chess/MultiplayerDialog';
import UserMenu from '@/components/auth/UserMenu';
import AuthModal from '@/components/auth/AuthModal';
import ResetPassword from '@/components/auth/ResetPassword';
import { useGameStore } from '@/store/gameStore';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Palette, 
  Volume2, 
  VolumeX, 
  Lightbulb,
  Plus,
  Play,
  Settings,
  BarChart3,
  Trophy,
  Users,
  Home,
  ChevronLeft
} from 'lucide-react';

const ChessGame = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const gameStatus = useGameStore(state => state.gameStatus);
  const playerColor = useGameStore(state => state.playerColor);
  const boardTheme = useGameStore(state => state.boardTheme);
  const soundEnabled = useGameStore(state => state.soundEnabled);
  const coachEnabled = useGameStore(state => state.coachEnabled);
  const showLegalMoves = useGameStore(state => state.showLegalMoves);
  const showCoordinates = useGameStore(state => state.showCoordinates);
  const reviewMode = useGameStore(state => state.reviewMode);
  const history = useGameStore(state => state.history);
  const setBoardTheme = useGameStore(state => state.setBoardTheme);
  const setSoundEnabled = useGameStore(state => state.setSoundEnabled);
  const setCoachEnabled = useGameStore(state => state.setCoachEnabled);
  const setShowLegalMoves = useGameStore(state => state.setShowLegalMoves);
  const setShowCoordinates = useGameStore(state => state.setShowCoordinates);
  const enterReviewMode = useGameStore(state => state.enterReviewMode);

  // Dialog state
  const showNewGame = gameStatus === 'idle';
  const [showNewGameManual, setShowNewGameManual] = useState(false);
  const [dialogDismissed, setDialogDismissed] = useState(false);
  const [saveLoadMode, setSaveLoadMode] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showMultiplayer, setShowMultiplayer] = useState(false);
  const [multiplayerRoomId, setMultiplayerRoomId] = useState(null);
  const [resetToken, setResetToken] = useState(null);

  // Check for URL parameters (reset token or room ID)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const room = params.get('room');
    
    if (token) {
      setResetToken(token);
    }
    if (room) {
      setMultiplayerRoomId(room);
      setShowMultiplayer(true);
    }
  }, []);

  const handleResetComplete = () => {
    setResetToken(null);
    setShowAuthModal(true);
    // Clear URL
    window.history.replaceState({}, document.title, window.location.pathname);
  };
  
  const handleStartMultiplayer = (config) => {
    // TODO: Connect to WebSocket and start multiplayer game
    console.log('Starting multiplayer:', config);
    // Clear URL parameter
    window.history.replaceState({}, document.title, window.location.pathname);
    setMultiplayerRoomId(null);
  };

  const handleNewGame = () => {
    setDialogDismissed(false); // Reset dismissed state when manually opening
    setShowNewGameManual(true);
  };

  const handleCloseNewGame = () => {
    setShowNewGameManual(false);
    setDialogDismissed(true); // Mark as dismissed so auto-open doesn't re-trigger
  };
  
  // Don't show new game dialog if reset password modal is active OR user dismissed it
  const shouldShowNewGameDialog = ((showNewGame && !dialogDismissed) || showNewGameManual) && !resetToken;

  return (
    <div 
      className={`
        min-h-screen
        ${boardTheme === 'cosmic'
          ? 'bg-obsidian'
          : 'bg-gradient-to-br from-amber-100 via-amber-50 to-orange-100'
        }
      `}
      data-testid="chess-game-page"
    >
      {/* Subtle pattern for cosmic theme */}
      {boardTheme === 'cosmic' && (
        <div className="fixed inset-0 bg-dot-pattern opacity-30 pointer-events-none" />
      )}
      
      {/* Prominent Header */}
      <header className={`
        sticky top-0 z-50 py-3 px-3 sm:px-6
        ${boardTheme === 'cosmic'
          ? 'bg-obsidian/90 backdrop-blur-md border-b border-slate-800'
          : 'bg-gradient-to-r from-amber-200 via-amber-100 to-amber-200 border-b border-amber-300'
        }
      `}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo - Responsive sizing */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Back Button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => isAuthenticated ? navigate('/dashboard') : navigate('/')}
              className={`
                mr-1 sm:mr-2
                ${boardTheme === 'cosmic'
                  ? 'text-slate-400 hover:text-white hover:bg-slate-800'
                  : 'text-amber-700 hover:text-amber-900 hover:bg-amber-200'
                }
              `}
              data-testid="back-btn"
            >
              <ChevronLeft className="w-4 h-4 sm:mr-1" />
              <span className="hidden sm:inline">{isAuthenticated ? 'Dashboard' : 'Home'}</span>
            </Button>
            
            <div className="flex items-center gap-2">
              <div className={`
                w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center
                ${boardTheme === 'cosmic'
                  ? 'bg-gradient-to-br from-cyan-500 to-emerald-500'
                  : 'bg-gradient-to-br from-amber-500 to-orange-500'
                }
              `}>
                <span className="text-white text-lg">♟</span>
              </div>
              <span className={`
                font-display text-lg sm:text-xl font-bold
                ${boardTheme === 'cosmic' ? 'text-white' : 'text-amber-900'}
              `}>
                Chess
              </span>
            </div>
          </div>
          
          {/* Header Actions - Responsive */}
          <div className="flex items-center gap-1.5 sm:gap-3">
            {/* Icon buttons - smaller on mobile */}
            <div className="flex items-center gap-1 sm:gap-2">
              {/* Theme Toggle */}
              <button
                onClick={() => setBoardTheme(boardTheme === 'cosmic' ? 'classic' : 'cosmic')}
                className={`
                  p-2 sm:p-2.5 rounded-lg transition-all
                  ${boardTheme === 'cosmic'
                    ? 'bg-slate-800 text-cyan-400 hover:bg-slate-700 border border-slate-700'
                    : 'bg-amber-200 text-amber-700 hover:bg-amber-300 border border-amber-400'
                  }
                `}
                title="Toggle theme"
              >
                <Palette className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              
              {/* Sound Toggle */}
              <button
                onClick={() => setSoundEnabled(!soundEnabled)}
                className={`
                  p-2 sm:p-2.5 rounded-lg transition-all
                  ${boardTheme === 'cosmic'
                    ? 'bg-slate-800 text-cyan-400 hover:bg-slate-700 border border-slate-700'
                    : 'bg-amber-200 text-amber-700 hover:bg-amber-300 border border-amber-400'
                  }
                `}
                title={soundEnabled ? 'Mute' : 'Unmute'}
              >
                {soundEnabled ? <Volume2 className="w-4 h-4 sm:w-5 sm:h-5" /> : <VolumeX className="w-4 h-4 sm:w-5 sm:h-5" />}
              </button>
              
              {/* Stats Button */}
              <button
                onClick={() => setShowStats(true)}
                className={`
                  p-2 sm:p-2.5 rounded-lg transition-all
                  ${boardTheme === 'cosmic'
                    ? 'bg-slate-800 text-cyan-400 hover:bg-slate-700 border border-slate-700'
                    : 'bg-amber-200 text-amber-700 hover:bg-amber-300 border border-amber-400'
                  }
                `}
                title="View Statistics"
                data-testid="stats-button"
              >
                <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              
              {/* Leaderboard Button */}
              <button
                onClick={() => setShowLeaderboard(true)}
                className={`
                  hidden sm:block p-2 sm:p-2.5 rounded-lg transition-all
                  ${boardTheme === 'cosmic'
                    ? 'bg-slate-800 text-amber-400 hover:bg-slate-700 border border-slate-700'
                    : 'bg-amber-200 text-amber-700 hover:bg-amber-300 border border-amber-400'
                  }
                `}
                title="Leaderboard"
                data-testid="leaderboard-button"
              >
                <Trophy className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
            
            {/* New Game Button */}
            <Button
              onClick={handleNewGame}
              size="sm"
              className={`
                text-xs sm:text-sm px-2 sm:px-4 font-medium
                ${boardTheme === 'cosmic'
                  ? 'bg-cyan-600 hover:bg-cyan-500 text-white'
                  : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white'
                }
              `}
              data-testid="new-game-btn"
            >
              <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">New Game</span>
            </Button>
            
            {/* Multiplayer Button - Icon only on mobile */}
            <Button
              onClick={() => setShowMultiplayer(true)}
              variant="outline"
              size="sm"
              className={`
                text-xs sm:text-sm px-2 sm:px-4
                ${boardTheme === 'cosmic'
                  ? 'border-slate-700 text-cyan-400 hover:bg-slate-800'
                  : 'border-green-500 text-green-700 hover:bg-green-50'
                }
              `}
              data-testid="multiplayer-button"
            >
              <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline">Play Friend</span>
            </Button>
            
            {/* User Menu / Login */}
            <UserMenu onLoginClick={() => setShowAuthModal(true)} />
          </div>
        </div>
      </header>

      {/* Main Content - Board Left, Info Right */}
      <main className="max-w-7xl mx-auto px-2 sm:px-4 py-4 sm:py-6 relative z-10">
        <div className="flex flex-col lg:flex-row gap-4 sm:gap-6">
          
          {/* Left Side - Eval Bar + Board */}
          <div className="flex gap-2 sm:gap-4 items-start justify-center lg:justify-start">
            {/* Eval Bar - Hidden on mobile */}
            <div className="hidden md:block">
              <EvalBar />
            </div>
            
            {/* Chess Board - Full width on mobile */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-[min(100vw-1rem,400px)] sm:max-w-none"
            >
              <ChessBoard />
            </motion.div>
          </div>
          
          {/* Right Side - Two Column Info Panel */}
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3 sm:gap-4">
            
            {/* Column 1 - Game Info & Coach */}
            <div className="space-y-4">
              {/* Player Status Card */}
              <div className={`
                p-4 rounded-xl
                ${boardTheme === 'cosmic'
                  ? 'bg-obsidian-card border border-slate-800'
                  : 'bg-white/80 border border-amber-200'
                }
              `}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`
                      w-4 h-4 rounded-full
                      ${playerColor === 'white' ? 'bg-white border border-gray-300' : 'bg-slate-900 border border-slate-600'}
                    `} />
                    <span className={`font-medium ${boardTheme === 'cosmic' ? 'text-white' : 'text-gray-900'}`}>
                      You ({playerColor})
                    </span>
                  </div>
                  <span className={`
                    text-xs font-medium px-2 py-1 rounded
                    ${gameStatus === 'playing'
                      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                      : 'bg-slate-500/10 text-slate-400 border border-slate-500/20'
                    }
                  `}>
                    {gameStatus === 'playing' ? 'Playing' : gameStatus}
                  </span>
                </div>
                
                {/* Coach Toggle */}
                <div className={`
                  flex items-center justify-between p-3 rounded-lg
                  ${boardTheme === 'cosmic' ? 'bg-obsidian border border-slate-800' : 'bg-amber-50'}
                `}>
                  <div className="flex items-center gap-2">
                    <Lightbulb className={`w-4 h-4 ${coachEnabled ? 'text-cyan-400' : 'text-slate-500'}`} />
                    <span className={`text-sm ${boardTheme === 'cosmic' ? 'text-slate-300' : 'text-gray-700'}`}>
                      Coach Mode
                    </span>
                  </div>
                  <Switch 
                    checked={coachEnabled} 
                    onCheckedChange={setCoachEnabled}
                    data-testid="coach-mode-toggle"
                  />
                </div>
              </div>
              
              {/* Coach Feedback (when not in review mode) */}
              {!reviewMode && <CoachFeedback />}
              
              {/* Game Review Panel (when in review mode) */}
              {reviewMode && <GameReview />}
              
              {/* Review Game Button */}
              {!reviewMode && history.length > 0 && gameStatus !== 'idle' && (
                <Button
                  variant="outline"
                  onClick={enterReviewMode}
                  className={`
                    w-full
                    ${boardTheme === 'cosmic'
                      ? 'bg-slate-800/50 border-slate-700 text-slate-300 hover:bg-slate-800'
                      : 'bg-purple-50 border-purple-200 text-purple-700 hover:bg-purple-100'
                    }
                  `}
                  data-testid="review-game-btn"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Review Game
                </Button>
              )}
            </div>
            
            {/* Column 2 - Move Quality & History */}
            <div className="space-y-4">
              {/* Move Quality Card (instead of popup) */}
              <MoveQualityCard />
              
              {/* Move History */}
              {!reviewMode && <MoveHistory />}
              
              {/* Controls */}
              <ControlBar 
                onSave={() => setSaveLoadMode('save')}
                onLoad={() => setSaveLoadMode('load')}
                onSettings={() => setShowSettings(!showSettings)}
              />
              
              {/* Settings Panel */}
              {showSettings && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className={`
                    p-4 rounded-xl space-y-3
                    ${boardTheme === 'cosmic'
                      ? 'bg-obsidian-card border border-slate-800'
                      : 'bg-white/80 border border-amber-200'
                    }
                  `}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Settings className={`w-4 h-4 ${boardTheme === 'cosmic' ? 'text-slate-400' : 'text-gray-500'}`} />
                    <h3 className={`font-medium text-sm ${boardTheme === 'cosmic' ? 'text-slate-300' : 'text-gray-700'}`}>
                      Settings
                    </h3>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${boardTheme === 'cosmic' ? 'text-slate-400' : 'text-gray-600'}`}>
                      Show Legal Moves
                    </span>
                    <Switch checked={showLegalMoves} onCheckedChange={setShowLegalMoves} />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${boardTheme === 'cosmic' ? 'text-slate-400' : 'text-gray-600'}`}>
                      Show Coordinates
                    </span>
                    <Switch checked={showCoordinates} onCheckedChange={setShowCoordinates} />
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Dialogs */}
      <NewGameDialog 
        isOpen={shouldShowNewGameDialog} 
        onClose={handleCloseNewGame} 
      />
      
      <SaveLoadDialog 
        isOpen={saveLoadMode !== null}
        onClose={() => setSaveLoadMode(null)}
        mode={saveLoadMode}
      />
      
      <GameOverDialog 
        onNewGame={handleNewGame}
      />
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />
      
      {/* Password Reset Modal */}
      {resetToken && (
        <ResetPassword 
          token={resetToken} 
          onComplete={handleResetComplete} 
        />
      )}
      
      {/* Stats Dialog */}
      <StatsDialog
        isOpen={showStats}
        onClose={() => setShowStats(false)}
        theme={boardTheme}
      />
      
      {/* Leaderboard Dialog */}
      <LeaderboardDialog
        isOpen={showLeaderboard}
        onClose={() => setShowLeaderboard(false)}
        theme={boardTheme}
      />
      
      {/* Multiplayer Dialog */}
      <MultiplayerDialog
        isOpen={showMultiplayer}
        onClose={() => {
          setShowMultiplayer(false);
          setMultiplayerRoomId(null);
          window.history.replaceState({}, document.title, window.location.pathname);
        }}
        theme={boardTheme}
        onStartMultiplayer={handleStartMultiplayer}
        initialRoomId={multiplayerRoomId}
      />
    </div>
  );
};

export default ChessGame;
